import { PostType, PostTypesResponse, PostTypeResponse } from '../types/postType'

const CRO_API_BASE_URL = import.meta.env.VITE_CRO_API_BASE_URL || 'http://localhost:3007/api/v1'
const timeoutMs = 30000

class PostTypeService {
  private baseURL: string

  constructor() {
    this.baseURL = CRO_API_BASE_URL
  }

  /**
   * Get all post types
   * @param platformId - Optional platform ID to filter by
   */
  async getPostTypes(platformId?: number): Promise<PostType[]> {
    const url = new URL(`${this.baseURL}/post-types`)
    if (platformId) {
      url.searchParams.append('platform_id', String(platformId))
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([
      fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }),
      timeoutPromise
    ])

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error?.message || `Failed to fetch post types: ${response.statusText}`)
    }

    const result: PostTypesResponse = await response.json()
    return result.data
  }

  /**
   * Get a single post type by ID
   */
  async getPostTypeById(id: string): Promise<PostType> {
    const url = new URL(`${this.baseURL}/post-types/${id}`)

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([
      fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }),
      timeoutPromise
    ])

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error?.message || `Failed to fetch post type: ${response.statusText}`)
    }

    const result: PostTypeResponse = await response.json()
    return result.data
  }
}

export const postTypeService = new PostTypeService()
export default postTypeService
